import React, { useState } from 'react';
import moment from 'moment';
import { fluid, fluidVh } from 'components/UserDashboardV2/utils/responsive';
import { /* getStatusBadgeStyle, */ formatTimeRange } from './utils';
import { ATTENDANCE_SUBJECT_COLORS } from './constants';
import AttendanceCalendarStrip from './AttendanceCalendarStrip';
import AttendanceSummaryActions from './AttendanceSummaryActions';
import AttendanceRequestsSection from './AttendanceRequestsSection';

import ArrowSquareLeft from '../dashboardV3/arrow-square-left.svg';
import ArrowSquareRight from '../dashboardV3/arrow-square-right.svg';
import SubjectCardIcon from '../dashboardV3/Group 1171277149.svg';
import BatchIcon from '../../../Assets/userManagementV3/BatchIcon.svg';
import NoDataIcon from '../../../Assets/ill-no-data.svg';

// Mode badge colours: Online green, Offline red, others blue.
const getModeBadgeStyle = (mode) => {
    const m = String(mode || "").toLowerCase();
    if (m === "online" || m === "broadcast") return { bg: "#ECFDF5", stroke: "#34D399", text: "#059669" };
    if (m === "offline") return { bg: "#FEF2F2", stroke: "#F87171", text: "#DC2626" };
    return { bg: "#ECFBFF", stroke: "#56D5FF", text: "#007FA7" };
};

const AttendanceTab = ({
    attendanceData,
    attendanceOverview,
    studentAttendanceData,
    selectedAttendanceCourseId,
    selectedAttendanceDate,
    selectedDate,
    attendanceLoading,
    showAttendanceBySubject,
    isTeacher,
    isStaffRoute,
    visibleMonthColumns,
    dynamicCalendarMonths,
    calendarScrollContainerRef,
    currentMonthRef,
    attendanceSummaryCardRef,
    attendanceSummaryHeight,
    handleAttendanceCourseChange,
    handleAttendanceDateClick,
    userId,
}) => {
    // Date picked from a calendar tooltip's "Mark attendance" — opens the drawer
    // (owned by AttendanceSummaryActions) with that date filled in.
    const [markAttendanceRequest, setMarkAttendanceRequest] = useState(null);
    const handleMarkAttendanceFromCalendar = (dateStr) => setMarkAttendanceRequest({ date: dateStr, at: Date.now() });

    const activeOverview = attendanceData?.overview || attendanceOverview?.overview || attendanceData;
    const activeTimesheet = attendanceData?.attendanceByTimesheet || attendanceOverview?.attendanceByTimesheet;

    const summary = studentAttendanceData?.summary;
    const overallDisplay = summary?.overallPct !== undefined
        ? `${summary.overallPct}%`
        : (activeOverview?.overallPercentage !== undefined
            ? (typeof activeOverview.overallPercentage === "number" ? `${activeOverview.overallPercentage}%` : `${activeOverview.overallPercentage}`)
            : "0%");

    const presentDisplay = summary?.present !== undefined
        ? String(summary.present)
        : (() => {
            const presentCount = activeOverview?.PresentCount ?? activeOverview?.presentCount ?? activeTimesheet?.presentDays ?? 0;
            const totalDaysOrClasses = activeOverview?.totalClasses || activeOverview?.totalDays || activeTimesheet?.totalDays;
            return totalDaysOrClasses ? `${presentCount}/${totalDaysOrClasses}` : `${presentCount}`;
        })();

    const partialDisplay = summary?.partiallyPresent !== undefined
        ? String(summary.partiallyPresent)
        : String(
            activeOverview?.HalfDayCount ?? activeOverview?.halfDayCount ?? activeTimesheet?.halfDays ?? activeOverview?.totalLeaves ?? 0
        );

    const absentDisplay = summary?.absent !== undefined
        ? String(summary.absent)
        : String(
            activeOverview?.AbsentCount ?? activeOverview?.absentCount ?? activeTimesheet?.absentDays ?? 0
        );

    const courses = studentAttendanceData?.courses || [];
    const activeCourseId = selectedAttendanceCourseId ?? studentAttendanceData?.selectedCourseId;
    const currentCourseIndex = courses.findIndex(c => String(c.courseId) === String(activeCourseId));
    const safeCourseIndex = currentCourseIndex >= 0 ? currentCourseIndex : 0;
    const currentCourse = courses[safeCourseIndex];

    const dayDetailList = Array.isArray(studentAttendanceData?.dayDetail)
        ? studentAttendanceData.dayDetail
        : [];

    const bySubjectList = Array.isArray(studentAttendanceData?.bySubject)
        ? studentAttendanceData.bySubject
        : [];

    return (
        // Whole tab scrolls as one: [left column | By subject] row, then the
        // full-width Attendance Requests section below both.
        <div
            className="staff-profile-info-scroll"
            style={{
                display: "flex",
                flexDirection: "column",
                gap: fluid(24),
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
            }}
        >
        <div style={{ display: "flex", gap: fluid(24), alignItems: "stretch", flexShrink: 0 }}>
            {/* Left Column */}
            <div
                style={{
                    flex: showAttendanceBySubject ? `1 1 ${fluid(882)}` : "1 1 100%",
                    minWidth: showAttendanceBySubject ? fluid(882) : 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: fluid(24),
                }}
            >
                {/* Attendance Summary Card */}
                <div ref={attendanceSummaryCardRef} style={{ width: "100%", minWidth: 0, backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: fluid(20) }}>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(12) }}>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>Attendance summary</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(12) }}>
                        {/* Web clock / mark attendance / download report — staff only */}
                        {isStaffRoute && userId && (
                            <AttendanceSummaryActions
                                userId={userId}
                                isTeacher={isTeacher}
                                markAttendanceRequest={markAttendanceRequest}
                                // Reload the tab (same date) so the marked attendance shows.
                                onAttendanceMarked={() => handleAttendanceDateClick(selectedAttendanceDate || selectedDate)}
                            />
                        )}
                        {/* Course switcher — student only */}
                        {showAttendanceBySubject && !isTeacher && currentCourse?.courseName && (
                            <div style={{ display: "flex", alignItems: "center", gap: fluid(12) }}>
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#191919" }}>
                                    {currentCourse.courseName}
                                </span>
                                {courses.length > 1 && (
                                    <div style={{ display: "flex", gap: fluid(6) }}>
                                        {safeCourseIndex > 0 && (
                                            <img
                                                src={ArrowSquareLeft}
                                                alt="Prev"
                                                onClick={() => handleAttendanceCourseChange(courses[safeCourseIndex - 1].courseId)}
                                                style={{ width: fluid(32), height: fluid(32), cursor: "pointer" }}
                                            />
                                        )}
                                        {safeCourseIndex < courses.length - 1 && (
                                            <img
                                                src={ArrowSquareRight}
                                                alt="Next"
                                                onClick={() => handleAttendanceCourseChange(courses[safeCourseIndex + 1].courseId)}
                                                style={{ width: fluid(32), height: fluid(32), cursor: "pointer" }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Download report by schedule — student only */}
                        {!isStaffRoute && userId && <AttendanceSummaryActions userId={userId} isStudent />}
                        </div>
                    </div>

                    {/* Stats Row */}
                    <div style={{ display: "flex", gap: fluid(16), marginBottom: fluid(24) }}>
                        {[
                            { label: "Overall", value: overallDisplay },
                            { label: "Present", value: presentDisplay },
                            { label: "Partially present", value: partialDisplay },
                            { label: "Absent", value: absentDisplay },
                        ].map((stat, idx) => (
                            <div key={idx} style={{ flex: 1, backgroundColor: "#F3F3F3", borderRadius: fluid(16), padding: `${fluid(12)} ${fluid(16)}`, height: fluid(77), boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, color: "#666666" }}>{stat.label}</span>
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#1A1A1A" }}>{stat.value}</span>
                            </div>
                        ))}
                    </div>

                    {/* Legend Row */}
                    <div style={{ display: "flex", gap: fluid(24), marginBottom: fluid(24), flexWrap: "wrap" }}>
                        {[
                            { label: "Present", color: "#C3F3D2" },
                            { label: "Partially present", color: "#FEE7A8" },
                            { label: "Absent", color: "#FFD7D8" },
                            { label: "Day off", color: "#F2F2F2" },
                            // Holiday / Leave — staff only.
                            ...(isStaffRoute ? [
                                { label: "Holiday", color: "#E2C3F3" },
                                { label: "Leave", color: "#C3E9F3" },
                            ] : []),
                            ...(!isStaffRoute ? [
                                { label: "No class", color: "#F1F1FF" },
                                { label: "Class cancelled", color: "#C3E9F3" },
                            ] : [])
                        ].map((item, idx) => (
                            <div key={idx} style={{ display: "flex", alignItems: "center", gap: fluid(8) }}>
                                <div style={{ width: fluid(16), height: fluid(16), borderRadius: fluid(4), backgroundColor: item.color }} />
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#666666" }}>{item.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Months Calendar Strip */}
                    <AttendanceCalendarStrip
                        calendarScrollContainerRef={calendarScrollContainerRef}
                        currentMonthRef={currentMonthRef}
                        visibleMonthColumns={visibleMonthColumns}
                        showAttendanceBySubject={showAttendanceBySubject}
                        dynamicCalendarMonths={dynamicCalendarMonths}
                        isStaffRoute={isStaffRoute}
                        isTeacher={isTeacher}
                        handleAttendanceDateClick={handleAttendanceDateClick}
                        onMarkAttendance={isStaffRoute && userId ? handleMarkAttendanceFromCalendar : undefined}
                    />
                </div>

                {/* Daily Records Card */}
                {showAttendanceBySubject && (
                    <div style={{ backgroundColor: "#FFFFFF", borderRadius: fluid(24), padding: fluid(24), boxSizing: "border-box", width: "100%" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(24), marginBottom: fluid(20), flexWrap: "wrap" }}>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#191919" }}>
                                {moment(selectedAttendanceDate || selectedDate).format("D MMMM YYYY, dddd")}
                            </span>
                            {/* Legend for the card borders below. */}
                            {[
                                { label: "Present", color: "#C3F3D2" },
                                { label: "Absent", color: "#FFD7D8" },
                                { label: "Class cancelled", color: "#C3E9F3" },
                            ].map((item) => (
                                <div key={item.label} style={{ display: "flex", alignItems: "center", gap: fluid(8) }}>
                                    <div style={{ width: fluid(16), height: fluid(16), borderRadius: fluid(4), backgroundColor: item.color }} />
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#666666" }}>{item.label}</span>
                                </div>
                            ))}
                        </div>

                        {dayDetailList.length > 0 ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: fluid(16) }}>
                                {dayDetailList.map((record, rIdx) => {
                                    const rawStatus = record.status;
                                    const isCancelledStatus = (() => {
                                        const s = String(rawStatus || "").toLowerCase().replace(/[\s_-]/g, "");
                                        return s === "cancelled" || s === "classcancelled" || s === "offday";
                                    })();
                                    const displayStatus = isCancelledStatus ? "Day off" : rawStatus;
                                    // Not used since the card has no border / status badge.
                                    // const badge = getStatusBadgeStyle(displayStatus);
                                    const statusKey = String(displayStatus || "").toLowerCase().replace(/[\s_-]/g, "");
                                    // Cancelled class: same colour as "Class cancelled" in the calendar.
                                    const rawKey = String(rawStatus || "").toLowerCase().replace(/[\s_-]/g, "");
                                    const isClassCancelled = rawKey === "cancelled" || rawKey === "classcancelled";
                                    // Present / Absent / Cancelled: tinted card background, no border (design).
                                    const cardBg = isClassCancelled ? "#C3E9F3"
                                        : statusKey === "present" ? "#E3F8E9"
                                        : statusKey === "absent" ? "#FDEFF0"
                                        : "#F9F9F9";
                                    const modeText = record.mode === "Broadcast" ? "Online" : (record.mode || "");
                                    const modeBadge = getModeBadgeStyle(record.mode);
                                    const timeStr = formatTimeRange(record.startTime, record.endTime) || record.time || "";
                                    const subTitle = isTeacher
                                        ? (record.batchSection || "")
                                        : (record.teacherName || record.teacher || "Teacher");
                                    return (
                                        <div key={record.occuranceId || rIdx} style={{ height: fluidVh(120), borderRadius: fluid(16), backgroundColor: cardBg, border: "none", padding: fluid(16), boxSizing: "border-box", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), color: "#666666" }}>{timeStr}</span>
                                                {/* Mode (Offline / Online) from the API; status is shown by the card border. */}
                                                {modeText ? (
                                                <span style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    height: fluid(22),
                                                    padding: `0 ${fluid(10)}`,
                                                    borderRadius: fluid(4),
                                                    backgroundColor: modeBadge.bg,
                                                    border: `1px solid ${modeBadge.stroke}`,
                                                    color: modeBadge.text,
                                                    fontFamily: "Roboto, sans-serif",
                                                    fontSize: fluid(12),
                                                    fontWeight: 600,
                                                    lineHeight: fluid(18),
                                                    letterSpacing: "0%",
                                                    boxSizing: "border-box",
                                                    whiteSpace: "nowrap"
                                                }}>
                                                    {modeText}
                                                </span>
                                                ) : null}
                                            </div>
                                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#1A1A1A", fontWeight: 500 }}>
                                                {record.subjectName || record.subject || "Subject"}
                                            </span>
                                            {subTitle ? (
                                                <div style={{ display: "flex", alignItems: "center", gap: fluid(8) }}>
                                                    <img src={isStaffRoute ? BatchIcon : SubjectCardIcon} alt="" style={{ width: fluid(16), height: fluid(16) }} />
                                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#666666" }}>
                                                        {subTitle}
                                                    </span>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: `${fluid(32)} 0`,
                                width: "100%",
                            }}>
                                <img src={NoDataIcon} alt="No data" style={{ width: fluid(180), height: fluid(140) }} />
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(18), fontWeight: 600, color: "#191919", marginTop: fluid(16), textAlign: "center" }}>
                                    No class attendance records for this date
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Right Column (By Subject) */}
            {showAttendanceBySubject && (
                // Spans the full left column (summary + day detail). The card is
                // absolutely filled so a long subject list scrolls inside it
                // instead of making the row taller.
                <div style={{ flex: `0 0 ${fluid(598)}`, width: fluid(598), position: "relative" }}>
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "#FFFFFF",
                            borderRadius: fluid(24),
                            padding: fluid(24),
                            boxSizing: "border-box",
                            display: "flex",
                            flexDirection: "column",
                            overflowY: "auto",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        <div>
                            <span style={{ display: "block", fontFamily: "Roboto, sans-serif", fontSize: fluid(20), fontWeight: 500, color: "#191919" }}>By subject</span>
                            {!isTeacher && currentCourse?.courseName ? (
                                <span style={{ display: "block", fontFamily: "Roboto, sans-serif", fontSize: fluid(16), color: "#191919", marginTop: fluid(4) }}>
                                    {currentCourse.courseName}
                                </span>
                            ) : null}
                        </div>

                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: fluid(20),
                            flex: 1,
                            minHeight: 0,
                            marginTop: fluid(20),
                            justifyContent: bySubjectList.length > 0 ? "flex-start" : "center",
                        }}>
                            {bySubjectList.length > 0 ? (
                                bySubjectList.map((subject, sIdx) => {
                                    const subjectText = subject.subjectName || subject.name || "";
                                    // Class start time from the API ("13:51:00" -> "1:51 PM"). Teachers see the subject name only.
                                    const startMoment = !isTeacher && subject.startTime ? moment(subject.startTime, ["HH:mm:ss", "HH:mm"]) : null;
                                    const startTimeText = startMoment && startMoment.isValid() ? startMoment.format("h:mm A") : "";
                                    const pctVal = typeof subject.pct === 'number' ? subject.pct : (parseFloat(subject.pct) || 0);
                                    const color = ATTENDANCE_SUBJECT_COLORS[sIdx % ATTENDANCE_SUBJECT_COLORS.length];
                                    return (
                                        <div key={subject.subjectId || sIdx} style={{ display: "flex", flexDirection: "column", gap: fluid(6) }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#1A1A1A", display: "inline-flex", alignItems: "center" }}>
                                                    {subjectText}
                                                    {startTimeText ? (
                                                        <>
                                                            <span style={{
                                                                display: "inline-block",
                                                                width: fluid(8),
                                                                height: fluid(8),
                                                                borderRadius: "50%",
                                                                backgroundColor: "#4B4B4B",
                                                                margin: `0 ${fluid(10)}`,
                                                            }} />
                                                            <span>{startTimeText}</span>
                                                        </>
                                                    ) : null}
                                                </span>
                                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#1A1A1A" }}>
                                                    {pctVal}%
                                                </span>
                                            </div>
                                            <div style={{ width: "100%", height: fluid(8), borderRadius: fluid(100), backgroundColor: "#E6E6E6", overflow: "hidden" }}>
                                                <div style={{ width: `${Math.min(100, Math.max(0, pctVal))}%`, height: "100%", borderRadius: fluid(100), backgroundColor: color }} />
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: `${fluid(32)} 0`,
                                    width: "100%",
                                }}>
                                    <img src={NoDataIcon} alt="No data" style={{ width: fluid(180), height: fluid(140) }} />
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(18), fontWeight: 600, color: "#191919", marginTop: fluid(16), textAlign: "center" }}>
                                        No subject attendance data available
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>

        {/* Attendance Requests — full width below both columns (same layout as Leave Requests, no MARK ATTENDANCE button) */}
        {userId && <AttendanceRequestsSection userId={userId} />}
        </div>
    );
};

export default AttendanceTab;
